import * as THREE from "three";
import { HqMaterials } from "../hqMaterials";
import type { BodyLook } from "./looks";

/**
 * Skin variants of the HQ set's lit materials (FINISH_PLAN WP8): a palette swap on the `_tex` atlas plus an optional
 * emissive trim, as a copy of the converted material that keeps every hook the original has (wind, haze, the lit
 * extras — `Material.clone` drops them, as HqMaterials.twin notes) and adds the skin's.
 *
 * Cost (the plan's budget: at most +1 material per skinned type): one variant per (source material, skin), shared by
 * every clone of that model, so a skinned tower type draws with one material in place of its stock one — the same
 * draw count. All variants compile to ONE extra program per source program (their cache key only adds "-skin"; the
 * look is uniforms). Meshes, bones, the bone atlas, static batching and the hub LOD are untouched: only the
 * `material` slot of a mesh changes, and back again when the skin comes off.
 *
 * The swap, in the albedo chunk: `mix(albedo, tint × luma(albedo) / RefLuma, mix) × gain` — the texture's own light
 * and dark survive, its hue goes toward the tint; strongly coloured texels are left alone (`KeepSatFrom`). The trim
 * adds `trim × smoothstep(TrimFrom, TrimTo, luma / RefLuma)`
 * to the emissive: the pale texels of the atlas (metal bands, rims, carved trims) light up; stone and wood do not.
 */
export class SkinMaterials {
  /** The painted atlases' typical albedo luma (linear): a texel this bright maps to the tint itself. */
  static readonly RefLuma = 0.3;
  /** The trim ramps in over this range of luma / RefLuma: only the palest texels carry it. */
  static readonly TrimFrom = 1.15;
  static readonly TrimTo = 1.9;
  /**
   * Readability: texels this saturated (HSV saturation) keep their painted colour. Stone, wood and iron are greys and
   * browns under ~0.5; the painted accents that tell towers apart — the archer's green roof, the lightning tower's
   * blue dome, the poison vats, the fire tower's coals — are well over it, so a skin restyles the masonry and leaves
   * those. (The frost crystal is pale, so it takes the skin's hue: its glow slot still reads ice blue.)
   */
  static readonly KeepSatFrom = 0.5;
  static readonly KeepSatTo = 0.75;
  private static readonly variants = new Map<THREE.Material, Map<string, THREE.MeshStandardMaterial>>();

  /** The skin `key`'s variant of a converted lit material (one per source and key). */
  static variant(source: THREE.MeshStandardMaterial, key: string, look: BodyLook): THREE.MeshStandardMaterial {
    let byKey = SkinMaterials.variants.get(source);
    if (!byKey) { byKey = new Map(); SkinMaterials.variants.set(source, byKey); }
    let v = byKey.get(key);
    if (v) return v;
    v = source.clone();
    v.name = `${source.name}~${key}`;
    const lin = (hex: number) => new THREE.Color(hex);          // hex is sRGB; Color stores it linear
    const trim = look.trim ? lin(look.trim.colour).multiplyScalar(look.trim.strength) : new THREE.Color(0, 0, 0);
    const u = {
      uSkinTint: { value: lin(look.tint) }, uSkinMix: { value: look.mix }, uSkinGain: { value: look.gain ?? 1 }, uSkinTrim: { value: trim },
      uSkinKeep: { value: look.keepAccents === false ? 0 : 1 },
    };
    if (look.metalness !== undefined) v.metalness = look.metalness;
    if (look.roughness !== undefined) v.roughness = look.roughness;
    const prev = source.onBeforeCompile;
    v.onBeforeCompile = (sh, r) => {
      prev.call(source, sh, r);
      Object.assign(sh.uniforms, u);
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform vec3 uSkinTint, uSkinTrim; uniform float uSkinMix, uSkinGain, uSkinKeep;\nfloat tdSkinL;")
        .replace("#include <map_fragment>", `#include <map_fragment>
  {
    tdSkinL = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722)) / ${SkinMaterials.RefLuma.toFixed(3)};
    // painted accents keep their colour: a tower's crystal, its flame, its roof paint say what it is
    vec3 c0 = diffuseColor.rgb;
    float sat = (max(max(c0.r, c0.g), c0.b) - min(min(c0.r, c0.g), c0.b)) / max(max(max(c0.r, c0.g), c0.b), 1e-4);
    float keep = smoothstep(${SkinMaterials.KeepSatFrom.toFixed(3)}, ${SkinMaterials.KeepSatTo.toFixed(3)}, sat) * uSkinKeep;
    diffuseColor.rgb = mix(c0, uSkinTint * tdSkinL, uSkinMix * (1.0 - keep)) * uSkinGain;
  }`)
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
  totalEmissiveRadiance += uSkinTrim * smoothstep(${SkinMaterials.TrimFrom.toFixed(3)}, ${SkinMaterials.TrimTo.toFixed(3)}, tdSkinL);`);
    };
    const key0 = source.customProgramCacheKey();
    v.customProgramCacheKey = () => `${key0}-skin`;
    const entry = HqMaterials.standard.find((e) => e.mat === source);
    HqMaterials.standard.push({ mat: v, ground: entry?.ground ?? false });
    byKey.set(key, v);
    return v;
  }

  /** Whether a mesh material takes a body skin: the lit atlas, not the glow, the pennant (its own dressing) or the pool. */
  static skinnable(m: THREE.Material): m is THREE.MeshStandardMaterial {
    return (m as THREE.MeshStandardMaterial).isMeshStandardMaterial === true && !/_team$/.test(m.name ?? "");
  }

  /**
   * Dresses every mesh under `root` in `look` (keyed `key`), or back in its stock material when `look` is null. The
   * stock material is remembered on the mesh the first time, so dressing again with another skin never stacks.
   */
  static dress(root: THREE.Object3D, key: string | null, look: BodyLook | null): void {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || Array.isArray(mesh.material)) return;
      const stock = (mesh.userData.tdSkinStock as THREE.Material | undefined) ?? mesh.material;
      if (!SkinMaterials.skinnable(stock)) return;
      if (!key || !look) {
        if (mesh.userData.tdSkinStock) { mesh.material = stock; delete mesh.userData.tdSkinStock; }
        return;
      }
      mesh.userData.tdSkinStock = stock;
      mesh.material = SkinMaterials.variant(stock, key, look);
    });
  }

  /**
   * The asset library is unloading `mats` (HqMaterials.forget): their variants go too, from the look list as well.
   * Returns the variants, which the caller disposes with the rest.
   */
  static forget(mats: ReadonlySet<THREE.Material>): THREE.Material[] {
    const gone: THREE.Material[] = [];
    for (const m of mats) {
      const byKey = SkinMaterials.variants.get(m);
      if (!byKey) continue;
      for (const v of byKey.values()) gone.push(v);
      SkinMaterials.variants.delete(m);
    }
    return gone;
  }
}
HqMaterials.forgetHooks.push((mats) => SkinMaterials.forget(mats));
